import {
  Dashboard as DashboardIcon,
  Notifications as NotificationsIcon,
  AccountBalance as BounsIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  keyframes,
  Paper,
  Slider,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setSuccess } from "../../Redux/AlertReducer";
import { Helper } from "../../tools/Helper";
import { api_Routes } from "../../tools/api_Routes";
import ReusableSettingsCard from "./SettingCard.tsx";
import ArticleIcon from "@mui/icons-material/Article";

// Animation keyframes
const slideIn = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

// Styled components
const SettingCard = styled(Paper)(({ theme }) => ({
  animation: `${slideIn} 0.3s ease-out`,
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
  },
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));
const Setting = ({ data }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [settings, setSettings] = useState({
    notify_before_days: 30,
    old_domain_years: 5,
    payment_methods: [],
    location: "",
    contactemail: "",
    contactphonenumber: "",
    free_ads_count:0,
    smm_tickets_token:"",
    auto_smm_tickets:0,
    cryptomus_bonus: 0,
    payeer_bonus: 0,
    free_bonus: 0,
    cryptomus_bonus_type: "Fixed",
    payeer_bonus_type: "Fixed",
  });
  const [newPaymentMethod, setNewPaymentMethod] = useState("");

  const handleNavigate = (slug) => {
    navigate(`/Settings/PageEditor/${slug}`);
  };
  useEffect(() => {
    if (data) {
      setSettings({
        notify_before_days: Number(data?.notify_before_days) || 30,
        old_domain_years: Number(data?.old_domain_years) || 5,
        free_ads_count: Number(data?.free_ads_count) || 5,
        payment_methods: data?.payment_methods?.split(",") || [],
        location: data?.location || "",
        contactemail: data?.contactemail || "",
        contactphonenumber: data?.contactphonenumber || "",
        twitter: data?.twitter || "",
        telegram: data?.telegram || "",
        youtube: data?.youtube || "",
        instagram: data?.instagram || "",
        facebook: data?.facebook || "",
        smm_tickets_token: data?.smm_tickets_token || "",
        auto_smm_tickets: data?.auto_smm_tickets === '1' ? 1 : 0 || 0,
        cryptomus_bonus: Number(data?.cryptomus_bonus) || 0,
        payeer_bonus: Number(data?.payeer_bonus) || 0,
        free_bonus: Number(data?.free_bonus) || 0,
        cryptomus_bonus_type: data?.cryptomus_bonus_type || "Fixed",
        payeer_bonus_type: data?.payeer_bonus_type || "Fixed",
      });
    }
  }, [data]);
  const handleAddPaymentMethod = () => {
    if (newPaymentMethod.trim().includes(",")) {
      alert("Commas are not allowed in payment method names");
      return;
    }
    if (
      newPaymentMethod.trim() &&
      !settings.payment_methods?.includes(newPaymentMethod.trim())
    ) {
      setSettings((prev) => ({
        ...prev,
        payment_methods: [...prev.payment_methods, newPaymentMethod.trim()],
      }));
      setNewPaymentMethod("");
    }
  };

  const handleDeletePaymentMethod = (method) => {
    setSettings((prev) => ({
      ...prev,
      payment_methods: prev.payment_methods.filter((m) => m !== method),
    }));
  };
  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };
  const handleSave = async () => {
    setIsLoadingSubmit(true);
    const formattedSettings = {
      ...settings,
      payment_methods: settings.payment_methods?.join(",") || "",
    };
    const payload = {
      settings: Object.entries(formattedSettings).map(([key, value]) => ({
        key,
        value: typeof value === "number" ? value.toString() : value,
      })),
    };

    try {
      const { response } = await Helper.Post({
        url: api_Routes.Settings.Edit,
        data: payload,
      });

      setIsLoadingSubmit(false);
      if (response) {
        dispatch(setSuccess(true));
        navigate("/Settings");
      }
    } catch (error) {
      console.error("An error occurred during form submission:", error);
      setIsLoadingSubmit(false);
    }
  };
  const settingsConfig = [
    {
      key: "old_domain_years",
      label: "years for old domains",
      value: settings.old_domain_years || 5,
      type: "number",
      inputProps: { min: 1, max: 100 },
    },
    {
      key: "free_ads_count",
      label: "Free Ads Count for users per month",
      value: settings.free_ads_count || 5,
      type: "number",
      inputProps: { min: 1, max: 100 },
    },
    // Update other fields to use string values
    {
      key: "location",
      label: "Location",
      type: "text",
      value: settings.location || "", // Initialize with empty string
    },
    {
      key: "contactemail",
      label: "Contact Email",
      type: "email",
      value: settings.contactemail || "",
    },
    {
      key: "contactphonenumber",
      label: "Contact Number",
      type: "text",
      value: settings.contactphonenumber || "",
    },
    {
      key: "twitter",
      label: "Twitter",
      type: "text",
      value: settings.twitter || "",
    },
    {
      key: "telegram",
      label: "Telegram",
      type: "text",
      value: settings.telegram || "",
    },
    {
      key: "youtube",
      label: "Youtube",
      type: "text",
      value: settings.youtube || "",
    },
    {
      key: "instagram",
      label: "Instagram",
      type: "text",
      value: settings.instagram || "",
    },
    {
      key: "facebook",
      label: "Facebook",
      type: "text",
      value: settings.facebook || "",
    },
    {
      key: "smm_tickets_token",
      label: "SmmTickets Token",
      type: "text",
      value: settings.smm_tickets_token || "",
    },
    {
      key: "auto_smm_tickets",
      label: "SmmTickets Auto use",
      type: "boolean",
      value: settings.auto_smm_tickets || 0,
    },
  ];
  return (
    <Box sx={{ p: 3, maxWidth: 800, margin: "0 auto" }}>
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <SettingsIcon sx={{ fontSize: 40, mb: 1 }} />
        <Typography variant="h4" gutterBottom>
          Admin Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage application-wide settings and preferences
        </Typography>
      </Box>
      <ReusableSettingsCard
        title="General Settings"
        settings={settingsConfig}
        onChange={handleSettingChange}
        icon={<DashboardIcon />}
      />
      <SettingCard elevation={3}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <BounsIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Bonus Settings</Typography>
        </Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            Bonus Configuration
          </Typography>

          {/* Cryptomus Bonus */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              <TextField
                  label="Cryptomus Bonus"
                  type="number"
                  value={settings.cryptomus_bonus || 0}
                  onChange={(e) => handleSettingChange("cryptomus_bonus", e.target.value)}
                  inputProps={{ min: 0 }}
                  sx={{ width: 200 }}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">Type:</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                      variant={settings.cryptomus_bonus_type === 'Fixed' ? "contained" : "outlined"}
                      size="small"
                      onClick={() => handleSettingChange("cryptomus_bonus_type", "Fixed")}
                  >
                    Fixed
                  </Button>
                  <Button
                      variant={settings.cryptomus_bonus_type === 'Percentage' ? "contained" : "outlined"}
                      size="small"
                      onClick={() => handleSettingChange("cryptomus_bonus_type", "Percentage")}
                  >
                    Percentage
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Payeer Bonus */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              <TextField
                  label="Payeer Bonus"
                  type="number"
                  value={settings.payeer_bonus || 0}
                  onChange={(e) => handleSettingChange("payeer_bonus", e.target.value)}
                  inputProps={{ min: 0 }}
                  sx={{ width: 200 }}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">Type:</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                      variant={settings.payeer_bonus_type === 'Fixed' ? "contained" : "outlined"}
                      size="small"
                      onClick={() => handleSettingChange("payeer_bonus_type", "Fixed")}
                  >
                    Fixed
                  </Button>
                  <Button
                      variant={settings.payeer_bonus_type === 'Percentage' ? "contained" : "outlined"}
                      size="small"
                      onClick={() => handleSettingChange("payeer_bonus_type", "Percentage")}
                  >
                    Percentage
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Free Bonus */}
          <Box sx={{ mb: 2 }}>
            <TextField
                label="Free Bonus"
                type="number"
                value={settings.free_bonus || 0}
                onChange={(e) => handleSettingChange("free_bonus", e.target.value)}
                inputProps={{ min: 0 }}
                sx={{ width: 200 }}
            />
          </Box>
        </Box>
      </SettingCard>
      <SettingCard elevation={3}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <NotificationsIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Notification Settings</Typography>
        </Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            Subscription Expiration Alert
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Slider
              value={settings.notify_before_days || 30}
              onChange={(e, val) =>
                handleSettingChange("notify_before_days", val)
              }
              min={1}
              max={60}
              sx={{ flexGrow: 1 }}
            />
            <Typography variant="body1">
              {settings.notify_before_days || 30} days
            </Typography>
          </Box>
        </Box>
      </SettingCard>
      <SettingCard elevation={3}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <DashboardIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Payment Methods</Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            Available Payment Methods
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              label="New Payment Method"
              value={newPaymentMethod}
              onChange={(e) => setNewPaymentMethod(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleAddPaymentMethod()}
            />
            <Button
              variant="contained"
              onClick={handleAddPaymentMethod}
              disabled={!newPaymentMethod.trim()}
            >
              Add
            </Button>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {settings.payment_methods?.map((method) => (
              <Paper
                key={method}
                sx={{
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  bgcolor: "background.paper",
                }}
              >
                <Typography>{method}</Typography>
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleDeletePaymentMethod(method)}
                >
                  Delete
                </Button>
              </Paper>
            ))}
          </Box>
        </Box>
      </SettingCard>
      <SettingCard elevation={3}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <ArticleIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Static Content</Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            Manage Content Sections
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {[
              { name: "About Us", slug: "about-us" },
              { name: "Terms", slug: "terms" },
            ].map((section) => (
              <Paper
                key={section.slug}
                sx={{
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  bgcolor: "background.paper",
                }}
              >
                <Typography>{section.name}</Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleNavigate(section.slug)}
                >
                  Change Section
                </Button>
              </Paper>
            ))}
          </Box>
        </Box>
      </SettingCard>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSave}
          sx={{ px: 4, py: 1.5 }}
        >
          {isLoadingSubmit ? "Saving..." : "Save Changes"}
        </Button>
      </Box>
    </Box>
  );
};
export default Setting;
